import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import { withFirstFramePoster } from '../../utils/video';

// Personality picker.
//   Desktop (>=768px): 4 cards in a row, fixed 222x354, click to select.
//   Mobile  (<768px):  swiper carousel of exactly 4 cards (no infinite buffer)
//     — pointer-events + GPU transforms, ported from the frontend /createaislut
//     swiper but trimmed to a finite, clamped 4-item track.
//
// Mobile sizes per design: neighbors 165x270, center 165x300 (same width, the
// center only grows in height + gets a selected border/checkmark).
//
// Card media is a still image by default. When GrowthBook supplies a `video`
// for a personality (createsexfriend-attributes[style].personality[value].video)
// the card plays that video instead, continuously — see CardMedia below.

const MOBILE_BREAKPOINT = 768;

const GAP = 8;
const EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const DURATION = '420ms';
const CAROUSEL_PADDING_Y = 72; // vertical room so the center card's glow isn't clipped
const LAYOUT_OVERHEAD = 160;
const MIN_SCALE = 0.6;
const DRAG_THRESHOLD = 8;

const MOBILE = { centerW: 165, centerH: 300, otherW: 165, otherH: 270 };
const DESKTOP_CARD = { w: 222, h: 354 };

const Checkmark = () => (
  <div
    style={{
      position: 'absolute',
      top: 12,
      right: 12,
      width: 20,
      height: 20,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #B14CEE, #8049F3)',
      boxShadow: '0 0 8px rgba(177,76,238,0.7)',
    }}
  >
    <svg width="9" height="9" viewBox="0 0 12 9" fill="none">
      <path
        d="M1 4L4.5 7.5L11 1"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const MEDIA_STYLE = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

// Card media: the GB-supplied `video` when there is one, otherwise the still
// `image`. The image is also the poster and the fallback if the video fails, so
// a personality with no video (or a broken URL) looks exactly as it did before.
//
// All four cards play continuously — muted, looped, no hover or selection
// needed, so the step looks the same whichever card the user is on. This is
// deliberately NOT wired to the MOUSEOVER arm of
// createsexfriend-animated-pictures: that arm makes an attribute card play only
// while hovered, which would leave three of four personality cards frozen. The
// OFF arm is still honoured (same as every other step) as the kill switch for
// video on the LP — it falls back to the still image.
const CardMedia = ({ item }) => {
  const { shouldShowVideos } = useVideoAnimation();
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);

  // `autoPlay` covers the normal path; this re-arms playback if the element was
  // paused out from under us — a background tab, or iOS suspending decode on a
  // card that was offscreen in the mobile carousel.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const play = () => {
      if (video.paused) video.play().catch(() => {});
    };

    play();
    video.addEventListener('canplay', play);
    document.addEventListener('visibilitychange', play);
    return () => {
      video.removeEventListener('canplay', play);
      document.removeEventListener('visibilitychange', play);
    };
  }, [shouldShowVideos, item.video, failed]);

  if (!shouldShowVideos || !item.video || failed) {
    return (
      <img
        src={item.image}
        alt={item.label}
        draggable={false}
        style={MEDIA_STYLE}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={withFirstFramePoster(item.video)}
      poster={item.image}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      onError={() => setFailed(true)}
      style={MEDIA_STYLE}
    />
  );
};

// Shared card visual. `highlighted` = selected (desktop) / center (mobile).
const CardVisual = ({ item, highlighted, labelSize }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      borderRadius: 16,
      boxSizing: 'border-box',
      padding: highlighted ? 4 : 1,
      background: highlighted
        ? 'linear-gradient(98.81deg, #B14CEE 6.57%, #8049F3 100%)'
        : '#FFFFFF1F',
      boxShadow: highlighted
        ? '0 0 30px rgba(177,76,238,0.5), 0 0 12px rgba(128,73,243,0.32)'
        : 'none',
      transition: `box-shadow ${DURATION} ${EASING}`,
    }}
  >
    <div
      style={{
        width: '100%',
        height: '100%',
        // Inner radius must equal outer (16) minus the border padding so the
        // rounded corners line up and the border isn't clipped at the corners.
        borderRadius: highlighted ? 12 : 15,
        overflow: 'hidden',
        position: 'relative',
        background:
          'radial-gradient(52.39% 52.39% at 50% 50%, #616161 0%, #000000 100%)',
      }}
    >
      <CardMedia item={item} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 52%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            color: '#fff',
            fontFamily: 'Poppins',
            fontWeight: 600,
            fontSize: labelSize,
          }}
        >
          {item.label}
        </span>
      </div>
      {highlighted && <Checkmark />}
    </div>
  </div>
);

const PersonalitySwiper = ({ options = [], selectedValue, onSelect }) => {
  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [winHeight, setWinHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );

  useEffect(() => {
    const onResize = () => {
      setWinWidth(window.innerWidth);
      setWinHeight(window.innerHeight);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = winWidth > 0 && winWidth < MOBILE_BREAKPOINT;

  // ---------------- Desktop: simple row of fixed-size cards ----------------
  if (!isMobile) {
    return (
      <div className="personality-swiper-row">
        {options.map((item) => (
          <button
            type="button"
            key={item.value}
            onClick={() => onSelect(item.value)}
            style={{
              width: DESKTOP_CARD.w,
              height: DESKTOP_CARD.h,
              border: 'none',
              background: 'none',
              padding: 0,
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
          >
            <CardVisual
              item={item}
              highlighted={selectedValue === item.value}
              labelSize={16}
            />
          </button>
        ))}
      </div>
    );
  }

  return (
    <PersonalityCarousel
      options={options}
      selectedValue={selectedValue}
      onSelect={onSelect}
      winHeight={winHeight}
    />
  );
};

// ---------------- Mobile: finite 4-card swiper carousel ----------------
const PersonalityCarousel = ({ options, selectedValue, onSelect, winHeight }) => {
  const COUNT = options.length;
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === selectedValue),
  );

  const [currentIdx, setCurrentIdx] = useState(selectedIndex);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Center cards relative to THIS container's width (not the window) — the
  // carousel lives inside the quiz form, not full-screen.
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const isPointerDown = useRef(false);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragBaseTX = useRef(0);

  // Shrink proportionally on very short viewports.
  const sizeScale =
    winHeight > 0
      ? Math.min(
          1,
          Math.max(
            MIN_SCALE,
            (winHeight - LAYOUT_OVERHEAD) /
              (MOBILE.centerH + CAROUSEL_PADDING_Y),
          ),
        )
      : 1;

  const CENTER_W = MOBILE.centerW * sizeScale;
  const CENTER_H = MOBILE.centerH * sizeScale;
  const OTHER_W = MOBILE.otherW * sizeScale;
  const OTHER_H = MOBILE.otherH * sizeScale;
  const CAROUSEL_H = CENTER_H + CAROUSEL_PADDING_Y;

  const CENTER_SCALE_X = CENTER_W / OTHER_W;
  const CENTER_SCALE_Y = CENTER_H / OTHER_H;
  const NEIGHBOR_SHIFT = (CENTER_W - OTHER_W) / 2;

  // Measure container width before paint, keep in sync on resize.
  useLayoutEffect(() => {
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const calcTX = useCallback(
    (idx) => containerWidth / 2 - OTHER_W / 2 - idx * (OTHER_W + GAP),
    [containerWidth, OTHER_W],
  );

  const reportSelection = useCallback(
    (idx) => {
      const value = options[idx]?.value;
      if (value && value !== selectedValue) onSelect(value);
    },
    [options, onSelect, selectedValue],
  );

  const goTo = useCallback(
    (idx) => {
      const clamped = Math.max(0, Math.min(COUNT - 1, idx));
      setIsTransitioning(true);
      setCurrentIdx(clamped);
      reportSelection(clamped);
    },
    [COUNT, reportSelection],
  );

  // Apply transform (skip while dragging).
  useLayoutEffect(() => {
    if (!trackRef.current || containerWidth === 0) return;
    if (isPointerDown.current) return;
    trackRef.current.style.transition = isTransitioning
      ? `transform ${DURATION} ${EASING}`
      : 'none';
    trackRef.current.style.transform = `translate3d(${calcTX(
      currentIdx,
    )}px, 0, 0)`;
  }, [currentIdx, isTransitioning, calcTX, containerWidth]);

  const onPointerDown = (e) => {
    isPointerDown.current = true;
    isDragging.current = false;
    dragStartX.current = e.clientX;
    dragBaseTX.current = calcTX(currentIdx);
    setIsTransitioning(false);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!isPointerDown.current) return;
    const offset = e.clientX - dragStartX.current;
    if (!isDragging.current) {
      if (Math.abs(offset) < DRAG_THRESHOLD) return;
      isDragging.current = true;
      if (trackRef.current) trackRef.current.style.transition = 'none';
    }
    e.preventDefault();
    // Rubber-band a bit at the edges instead of dragging into empty space.
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${
        dragBaseTX.current + offset
      }px, 0, 0)`;
    }
  };

  const onPointerUp = (e) => {
    isPointerDown.current = false;
    if (!isDragging.current) {
      // Tap on a neighbor → focus it.
      if (trackRef.current) {
        const trackLeft = trackRef.current.getBoundingClientRect().left;
        const relX = dragStartX.current - trackLeft;
        const tappedIdx = Math.floor(relX / (OTHER_W + GAP));
        if (tappedIdx >= 0 && tappedIdx < COUNT && tappedIdx !== currentIdx) {
          goTo(tappedIdx);
        }
      }
      return;
    }
    isDragging.current = false;
    const diff = dragStartX.current - e.clientX;
    if (Math.abs(diff) > 50) {
      goTo(currentIdx + (diff > 0 ? 1 : -1));
    } else {
      // Snap back to the current card.
      setIsTransitioning(true);
      if (trackRef.current) {
        trackRef.current.style.transition = `transform 0.3s ${EASING}`;
        trackRef.current.style.transform = `translate3d(${calcTX(
          currentIdx,
        )}px, 0, 0)`;
      }
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        height: CAROUSEL_H,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        cursor: 'grab',
        touchAction: 'pan-y',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        ref={trackRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: GAP,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        {options.map((item, idx) => {
          const dist = idx - currentIdx;
          const absDist = Math.abs(dist);
          const isCenter = dist === 0;
          const opacity = Math.max(0.3, 1 - absDist * 0.28);

          return (
            <div
              key={item.value}
              style={{
                flexShrink: 0,
                width: OTHER_W,
                height: OTHER_H,
                opacity,
                zIndex: isCenter ? 2 : 1,
                position: 'relative',
                cursor: isCenter ? 'default' : 'pointer',
                transform: isCenter
                  ? `translateX(0px) scale(${CENTER_SCALE_X}, ${CENTER_SCALE_Y})`
                  : `translateX(${
                      dist > 0 ? NEIGHBOR_SHIFT : -NEIGHBOR_SHIFT
                    }px) scale(1, 1)`,
                transformOrigin: 'center center',
                transition: `transform ${DURATION} ${EASING}, opacity ${DURATION} ${EASING}`,
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
              onClick={() => {
                if (!isCenter) goTo(idx);
              }}
            >
              <CardVisual item={item} highlighted={isCenter} labelSize={13} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PersonalitySwiper;
